import React from 'react';

type PlanKey = 'free' | 'basic' | 'premium';

type Props = {
  currentPlan?: string | null;
  onRequestUpgrade: (plan: 'basic' | 'premium', message: string) => Promise<void>;
};

const plans: Array<{
  key: PlanKey;
  name: string;
  price: string;
  description: string;
  features: string[];
}> = [
  {
    key: 'free',
    name: '無料お試し',
    price: '0円',
    description: 'まず価値を試すためのプラン',
    features: [
      'サジェスト検索 累計10回',
      '各コスト系 累計1回',
      '検索履歴・保存キーワード',
    ],
  },
  {
    key: 'basic',
    name: '基本プラン',
    price: '980円/月',
    description: '定期的なキーワード調査向け',
    features: [
      'サジェスト検索 無制限',
      '各コスト系 月10回',
      'ペルソナ画像 月5回',
    ],
  },
  {
    key: 'premium',
    name: 'プレミアムプラン',
    price: '3,980円/月',
    description: '深く継続的に分析する方向け',
    features: [
      'サジェスト検索 無制限',
      '各コスト系 月100回',
      'ペルソナ画像 月30回',
    ],
  },
];

const PlanUpgradeCard: React.FC<Props> = ({ currentPlan = 'free', onRequestUpgrade }) => {
  const [notice, setNotice] = React.useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = React.useState<'basic' | 'premium' | null>(null);
  const [message, setMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const selectedPlanName = plans.find((plan) => plan.key === selectedPlan)?.name || '';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedPlan || sending) return;

    setSending(true);
    setError(null);
    setNotice(null);
    try {
      await onRequestUpgrade(selectedPlan, message);
      setNotice(`${selectedPlanName}の先行利用申請を送信しました。確認後にご連絡します。`);
      setSelectedPlan(null);
      setMessage('');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : '申請を送信できませんでした。');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="rounded border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">プラン変更</h2>
          <p className="mt-1 text-sm text-gray-500">
            利用量に合わせて、分析できる回数を増やせます。
          </p>
        </div>
        <span className="w-fit rounded bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
          決済機能は準備中
        </span>
      </div>

      {notice && (
        <div className="mt-4 rounded border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
          {notice}
        </div>
      )}

      {error && (
        <div className="mt-4 rounded border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}

      {selectedPlan && (
        <form onSubmit={handleSubmit} className="mt-4 rounded border border-blue-100 bg-blue-50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-blue-800">
                {selectedPlanName}の先行利用を申請
              </p>
              <p className="mt-1 text-xs leading-5 text-blue-700">
                決済連携までは、申請内容を確認して手動でプラン変更します。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedPlan(null)}
              className="rounded p-1 text-blue-400 hover:bg-white hover:text-blue-700"
              aria-label="閉じる"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={3}
            placeholder="利用予定や相談したいことがあれば入力してください"
            className="mt-3 w-full rounded border border-blue-100 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
          />
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={sending}
              className="rounded bg-blue-500 px-4 py-2 text-sm font-bold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? '送信中...' : '申請を送信'}
            </button>
          </div>
        </form>
      )}

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
        {plans.map((plan) => {
          const isCurrent = plan.key === currentPlan;
          const isPremium = plan.key === 'premium';

          return (
            <div
              key={plan.key}
              className={`rounded border p-4 ${
                isCurrent
                  ? 'border-blue-200 bg-blue-50'
                  : isPremium
                    ? 'border-slate-200 bg-slate-50'
                    : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-gray-900">{plan.name}</h3>
                  <p className="mt-1 text-sm text-gray-500">{plan.description}</p>
                </div>
                {isCurrent && (
                  <span className="shrink-0 rounded bg-blue-500 px-2 py-1 text-xs font-bold text-white">
                    利用中
                  </span>
                )}
              </div>

              <p className="mt-4 text-2xl font-bold text-gray-900">{plan.price}</p>

              <ul className="mt-4 space-y-2 text-sm text-gray-600">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex gap-2">
                    <i className="fa-solid fa-check mt-0.5 text-blue-500"></i>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                disabled={isCurrent}
                onClick={() => {
                  if (plan.key === 'basic' || plan.key === 'premium') {
                    setSelectedPlan(plan.key);
                    setNotice(null);
                    setError(null);
                  }
                }}
                className={`mt-5 w-full rounded px-4 py-2 text-sm font-bold ${
                  isCurrent
                    ? 'cursor-default bg-white text-blue-600'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {isCurrent ? '現在のプラン' : 'アップグレード'}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PlanUpgradeCard;
